"use client";

import Link from "next/link";
import Image from "next/image";
import React from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import RelativeTime from "../relative-time";
import TablePagination from "@/components/primitives/table-pagination";

interface Article {
  id?: number | string;
  attributes: {
    title: string;
    league: string;
    image: any;
    publishedAt: string;
    slug: string;
  };
}

interface FeaturedNewsProps {
  articles: Article[];
  page?: number;
  size?: number;
  count?: number;
}

export default function FeaturedNews({
  articles,
  page,
  size,
  count,
}: FeaturedNewsProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const hasPagination =
    typeof page === "number" &&
    typeof size === "number" &&
    typeof count === "number";

  function updateParams(nextPage: number, nextSize: number) {
    const params = new URLSearchParams(searchParams.toString());

    params.set("page", String(nextPage));
    params.set("size", String(nextSize));

    router.push(`${pathname}?${params.toString()}`);
  }

  function onPageChange(newPage: number) {
    if (!hasPagination || typeof size !== "number") return;
    updateParams(newPage, size);
  }

  function onPageSizeChange(newSize: number) {
    if (!hasPagination) return;
    updateParams(1, newSize);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 desktop-news:grid-cols-2 desktop-grid:grid-cols-2 gap-4">
        {articles?.map((article, index) => {
          const { title, league, publishedAt, image, slug } = article.attributes;

          const thumbnailUrl =
            image?.data?.attributes?.formats?.medium?.url ||
            image?.data?.attributes?.url ||
            "/images/article-fallback.webp";

          return (
            <Link
              href={`/news/${slug}`}
              prefetch={false}
              key={article.id ?? index}
              className="flex flex-col gap-2 bg-white rounded-xl shadow-card relative"
            >
              <div className="image overflow-hidden rounded-xl">
                <Image
                  src={thumbnailUrl}
                  unoptimized
                  width={364}
                  height={260}
                  alt={title}
                  className="w-full aspect-video h-[260px] object-cover"
                />

                <div className="absolute inset-0 z-10 flex items-start flex-col bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.6)_100%)] bg-cover bg-center bg-no-repeat rounded-xl">
                  <div className="px-6 py-5 flex flex-col gap-2 items-start justify-between h-full">
                    <div className="flex gap-1 h-7 items-center text-title-sm-medium text-secondary-700 px-2 py-3 rounded-xl bg-[rgba(255,255,255,0.15)] backdrop-blur-[15.25px]">
                      <div>{league}</div>
                      <div>•</div>
                      <div>
                        <RelativeTime short date={publishedAt} />
                      </div>
                    </div>
                    <div className="text-headline-lg uppercase text-white font-bold">
                      {title}
                    </div>
                    
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {hasPagination ? (
        <TablePagination
          page={page}
          size={size}
          count={count}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          buttonColor="light"
        />
      ) : null}
    </div>
  );
}